import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";
import { commonButtonLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import { DealerPortalKundresaTab } from "@/components/dealer-portal/DealerPortalKundresaTab";
import { DealerPortalCustomersTab } from "@/components/dealer-portal/DealerPortalCustomersTab";
import { DealerPortalVehiclesTab } from "@/components/dealer-portal/DealerPortalVehiclesTab";
import { DealerPortalCompanyDialog } from "@/components/dealer-portal/DealerPortalCompanyDialog";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Building2,
  Info,
  UserPlus,
  Users,
  Flag,
  ListChecks,
  Car,
  ContactRound,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { dealerPortalLabels } from "@/i18n";
import { NativeImage } from "@/components/native/NativeImage";

const VALID_TABS = ["journey", "customers", "vehicles"] as const;

export default function DealerPortal() {
  const { language } = useLanguage();
  const d = dealerPortalLabels(language);
  const tabs = d.tabs as {
    journey?: string;
    customers?: string;
    vehicles?: string;
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") ?? "journey";
  const tab: (typeof VALID_TABS)[number] = (
    VALID_TABS as readonly string[]
  ).includes(tabFromUrl)
    ? (tabFromUrl as (typeof VALID_TABS)[number])
    : "journey";
  const openJourneyId = searchParams.get("open") ?? null;

  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const showPageBack = useShowNavigateBack();
  const backLabel = commonButtonLabels(getLanguage()).back;
  const { company, companyLogoUrl } = useDealerPortal();
  const companyData = company.data;
  const isLoading = company.isLoading;

  const setTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    if (value !== "journey") next.delete("open");
    setSearchParams(next, { replace: true });
  };

  const setOpenJourneyId = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) {
      next.set("tab", "journey");
      next.set("open", id);
    } else next.delete("open");
    setSearchParams(next, { replace: true });
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-3 px-1 sm:px-0">
        {/* Rad 1: Tillbaka + titel + infoknapp (på mobil egen rad; på desktop samma rad som företagskort) */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {showPageBack ? (
              <BtnNavigateBack
                label={backLabel}
                to="/dashboard"
                className="shrink-0 mb-0"
              />
            ) : (
              <Button variant="ghost" size="icon" className="shrink-0" asChild>
                <Link to="/dashboard">
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              </Button>
            )}
            <h1 className="text-lg sm:text-2xl font-semibold tracking-tight truncate">
              {d.title}
            </h1>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
                  aria-label={d.whatIsAria}
                >
                  <Info className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">{d.whatIsTitle}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[min(90vw,380px)] p-0 overflow-hidden rounded-lg border shadow-lg flex flex-col max-h-[min(70vh,420px)]"
                align="start"
                sideOffset={8}
              >
                <div className="bg-gradient-to-br from-muted/50 to-muted/30 px-3 py-2.5 border-b shrink-0">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Info className="h-4 w-4" />
                    </div>
                    <h3 className="font-semibold text-foreground text-sm">
                      {d.whatIsTitle}
                    </h3>
                  </div>
                </div>
                <div className="p-3 space-y-3 text-xs overflow-y-auto min-h-0">
                  <p className="text-muted-foreground leading-snug">
                    {d.whatIsIntro}
                  </p>
                  <div className="rounded-md bg-background/80 border p-2.5 space-y-2">
                    <p className="font-medium text-foreground flex items-center gap-1.5 text-xs">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold">
                        1
                      </span>
                      {d.whatIsCustomerJourney}
                    </p>
                    <p className="text-muted-foreground leading-snug pl-5">
                      {d.whatIsCustomerJourneyDesc}
                    </p>
                    <p className="text-muted-foreground leading-snug pl-5 text-[11px] italic">
                      {d.whatIsCustomerJourneyNote}
                    </p>
                    <div className="pl-5 space-y-1">
                      <p className="font-medium text-foreground/90">
                        {d.whatIsOnEachCard}
                      </p>
                      <ul className="space-y-1 text-muted-foreground">
                        <li className="flex items-start gap-1.5">
                          <UserPlus className="h-3 w-3 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />
                          <span>
                            <strong className="text-foreground/80">
                              {d.assignColleague}
                            </strong>{" "}
                            – {d.assignColleagueDesc}
                          </span>
                        </li>
                        <li className="flex items-start gap-1.5">
                          <Flag className="h-3 w-3 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                          <span>
                            <strong className="text-foreground/80">
                              {d.priority}
                            </strong>{" "}
                            – {d.priorityDesc}
                          </span>
                        </li>
                        <li className="flex items-start gap-1.5">
                          <Users className="h-3 w-3 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
                          <span>
                            <strong className="text-foreground/80">
                              {d.atInComments}
                            </strong>{" "}
                            – {d.atInCommentsDesc}
                          </span>
                        </li>
                        <li className="flex items-start gap-1.5">
                          <ListChecks className="h-3 w-3 shrink-0 mt-0.5 text-purple-600 dark:text-purple-400" />
                          <span>
                            <strong className="text-foreground/80">
                              {d.status}
                            </strong>{" "}
                            – {d.statusDesc}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="rounded-md bg-background/80 border p-2.5 space-y-2">
                    <p className="font-semibold text-foreground flex items-center gap-1.5 text-xs">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-sky-500/15 text-sky-600 dark:text-sky-400 text-[10px] font-bold">
                        2
                      </span>
                      <ContactRound className="h-3.5 w-3.5 shrink-0 text-sky-600 dark:text-sky-400" />
                      {d.whatIsCustomers}
                    </p>
                    <p className="text-muted-foreground leading-snug pl-5">
                      {d.whatIsCustomersDesc}
                    </p>
                  </div>
                  <div className="rounded-md bg-background/80 border p-2.5 space-y-2">
                    <p className="font-semibold text-foreground flex items-center gap-1.5 text-xs">
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-violet-500/15 text-violet-600 dark:text-violet-400 text-[10px] font-bold">
                        3
                      </span>
                      <Car className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-400" />
                      {d.whatIsVehicles}
                    </p>
                    <p className="text-muted-foreground leading-snug pl-5">
                      {d.whatIsVehiclesDesc}
                    </p>
                  </div>
                  <p className="text-muted-foreground leading-snug">
                    {d.moreFeatures}
                  </p>
                  <p className="text-muted-foreground leading-snug pt-1 border-t border-border/60">
                    {d.feedbackHint}{" "}
                    <span className="text-foreground font-medium">
                      {d.feedbackLink}
                    </span>
                    .
                  </p>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {/* Företagskort: egen rad på mobil, samma rad på desktop */}
          {isLoading ? (
            <div className="h-[52px] rounded-xl border border-dashed border-muted-foreground/25 flex items-center px-4 text-muted-foreground text-sm w-full sm:w-auto">
              {d.loading}
            </div>
          ) : companyData ? (
            <button
              type="button"
              onClick={() => setCompanyDialogOpen(true)}
              className="flex items-center gap-3 rounded-xl bg-transparent p-3 sm:min-w-[220px] shrink-0 transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:ring-offset-2 w-full sm:w-auto"
            >
              <div className="flex h-10 w-10 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-xl bg-gray-500/30 dark:bg-gray-500/60 border border-border overflow-hidden text-emerald-600 dark:text-emerald-400">
                {companyLogoUrl ? (
                  <NativeImage
                    src={companyLogoUrl}
                    alt=""
                    className="h-full w-full object-contain rounded-lg"
                  />
                ) : (
                  <Building2 className="h-5 w-5 sm:h-6 sm:w-6" />
                )}
              </div>
              <div className="text-center min-w-0 flex-1 flex items-center justify-center">
                <p className="font-semibold text-foreground text-sm sm:text-base truncate">
                  {companyData.name || d.company}
                </p>
              </div>
            </button>
          ) : null}
        </div>

        {companyData && (
          <DealerPortalCompanyDialog
            open={companyDialogOpen}
            onOpenChange={setCompanyDialogOpen}
            company={companyData}
            companyLogoUrl={companyLogoUrl ?? undefined}
          />
        )}

        <Tabs value={tab} onValueChange={setTab} className="w-full mt-2">
          <TabsList className="grid grid-cols-3 max-w-md h-9 sm:h-10 mx-auto font-normal text-xs sm:text-sm">
            <TabsTrigger
              value="journey"
              className="text-xs sm:text-sm font-normal"
            >
              {tabs.journey ?? "Kundresa"}
            </TabsTrigger>
            <TabsTrigger
              value="customers"
              className="text-xs sm:text-sm font-normal"
            >
              {tabs.customers ?? "Kunder"}
            </TabsTrigger>
            <TabsTrigger
              value="vehicles"
              className="text-xs sm:text-sm font-normal"
            >
              {tabs.vehicles ?? "Fordon"}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="journey" className="mt-4">
            <DealerPortalKundresaTab
              initialOpenJourneyId={openJourneyId}
              onOpenJourneyId={setOpenJourneyId}
              onClearOpenJourneyId={() => setOpenJourneyId(null)}
            />
          </TabsContent>
          <TabsContent value="customers" className="mt-4">
            <DealerPortalCustomersTab />
          </TabsContent>
          <TabsContent value="vehicles" className="mt-4">
            <DealerPortalVehiclesTab />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
